/**
 * Pure view rules of the Bunker Route deep-link page (voycalc.com/?page=bunker):
 * the forward-rate month pick shared with the Dataloy widget and the VoyCalc app's
 * Bunkering Options card, formatting, and the availability default.
 *
 * Tests: bunkerRouteView.test.ts (CRA jest) — docs/public-site.md.
 */

export interface CurvePoint {
  date: string; // "YYYY-MM"
  value: number;
}

export interface ForwardLeg {
  curve?: CurvePoint[] | null;
  flatValue?: number | null;
}

export const fmtDateTime = (iso?: string | null): string => {
  if (!iso) return '-';
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso; // date-only price stamps
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export const fmtMonth = (yyyyMm: string): string => {
  const d = new Date(`${yyyyMm}-01T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return yyyyMm;
  return d.toLocaleString('en', { month: 'short', year: '2-digit', timeZone: 'UTC' });
};

export const fmtRate = (value: number | null | undefined): string =>
  value == null ? '-' : value.toLocaleString('en', { maximumFractionDigits: 2 });

/** "YYYY-MM" of the first month forwards exist for: the month after `now` (spot covers the current one). */
export const firstForwardMonth = (now: Date = new Date()): string => {
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * The forward-curve point to show for a port: eligible months are those from next
 * month on (stored curves may hold current / past months — filter, never trust
 * order); the port's referenceEta (server-stamped, UTC) picks the month — its exact
 * month if present, else the nearest eligible month at-or-before it, else the
 * earliest eligible one (also the fallback without an ETA). null when the curve
 * has no eligible point.
 */
export const forwardPoint = (leg: ForwardLeg, referenceEta?: string | null, now: Date = new Date()): CurvePoint | null => {
  const nextKey = firstForwardMonth(now);
  const eligible = (leg.curve || []).filter((p) => p.date >= nextKey).sort((a, b) => a.date.localeCompare(b.date));
  if (eligible.length === 0) return null;
  const target = referenceEta && /^\d{4}-\d{2}/.test(referenceEta) ? referenceEta.slice(0, 7) : null; // ISO UTC → "YYYY-MM"
  if (!target) return eligible[0];
  const exact = eligible.find((p) => p.date === target);
  if (exact) return exact;
  const atOrBefore = eligible.filter((p) => p.date <= target);
  return atOrBefore.length > 0 ? atOrBefore[atOrBefore.length - 1] : eligible[0];
};

/** The forward cell: "<rate> (<Mon YY>)", else the flat value, else "-". */
export const forwardRate = (leg: ForwardLeg, referenceEta?: string | null, now: Date = new Date()): string => {
  const point = forwardPoint(leg, referenceEta, now);
  return point ? `${fmtRate(point.value)} (${fmtMonth(point.date)})` : fmtRate(leg.flatValue);
};

/** An unset availability level means the FIRST configured status (the desk's default). */
export const availabilityLevel = (level: string | null | undefined, statuses: { value: string }[]): string | undefined =>
  level || statuses[0]?.value;
